import { EMPTY_VALUE } from "@/constants";
import { orEmptyValue } from "@/lib/hcbb-cost-estimator-utils";
import { HcbbTruncatedText } from "@/pages/components/hcbb-cost-estimator/hcbb-truncated-text";
import type { HcbbProfessionalResult } from "@/model/hcbb-cost-estimator";

/**
 * What the provider brings to the appointment: the languages they speak and
 * where they trained.
 *
 * A description list rather than headings — these are label/value pairs, not
 * sections of the page, and turning them into headings would put three stops
 * in the document outline that lead nowhere.
 */
export const HcbbProviderCredentials = ({
  professional,
}: {
  professional: HcbbProfessionalResult;
}) => {
  // The payload uses "NA" as its empty sentinel, so a list of them is empty.
  const languages = (professional.languages ?? [])
    .map((language) => orEmptyValue(language))
    .filter((language) => language !== EMPTY_VALUE);

  const { institution } = professional;

  return (
    <dl className="grid gap-4 sm:grid-cols-3">
      <Credential
        label="Languages"
        value={languages.length > 0 ? languages.join(", ") : EMPTY_VALUE}
      />
      <Credential
        label="Education & Training"
        value={orEmptyValue(institution?.name)}
      />
      <Credential
        label="Year Completed"
        // Year 0 is not a year, so falsiness is safe here — unlike money.
        value={institution?.year ? String(institution.year) : EMPTY_VALUE}
      />
    </dl>
  );
};

/**
 * One label and its value.
 *
 * The label is uppercased in CSS rather than in the string, so a screen reader
 * reads a word instead of spelling out what it takes for an acronym.
 *
 * The value is clipped: a list of languages or a school's full legal name runs
 * far past a third of the row, and wrapping would leave the three cards at
 * different heights. The whole string stays in the DOM either way — see
 * `HcbbTruncatedText`.
 */
const Credential = ({ label, value }: { label: string; value: string }) => (
  <div className="border-1 border-gray min-w-0 rounded-lg border p-4 shadow-sm">
    <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
    <dd className="text-medium font-bold text-gray-800">
      <HcbbTruncatedText text={value} />
    </dd>
  </div>
);
